import { IterateDataOutput, IterateStep } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { Group, HoverCard, Stack, Text } from '@mantine/core';
import { DatasetType } from '@/types/automationFields.ts';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import React from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi';
import { generateUid } from '@/utils/misc.ts';
import { DEFAULT_DYNAMIC_DATA_INPUT } from '@/utils/constants.ts';
import IterateDataOutputFields from '@/pages/Editor/components/StepSidebar/StepForms/IterateStep/IterateDataOutputFields.tsx';
import StepFieldsAddButton from '@/pages/Editor/components/StepFieldsAddButton.tsx';

type props = {
  step: IterateStep;
};

type Fields = {
  input_has_header_row: boolean;
  data_outputs: IterateDataOutput[];
};

const IterateFields = ({ step }: props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });

  return (
    <Stack>
      <StyledSwitch
        label={
          <Group gap={'4px'} wrap={'nowrap'}>
            <StyledInputLabel size="sm" mb={0}>
              Input Data has header row?
            </StyledInputLabel>
            <HoverCard width={350} shadow="md">
              <HoverCard.Target>
                <HiOutlineInformationCircle size={12} />
              </HoverCard.Target>
              <HoverCard.Dropdown>
                <Text size="sm">
                  If the input dataset contains a header row (e.g., column names in a table or
                  spreadsheet), skip that row during processing. The header is supplied separately
                  to the child dataset as metadata.
                </Text>
              </HoverCard.Dropdown>
            </HoverCard>
          </Group>
        }
        labelPosition={'left'}
        checked={fields.input_has_header_row}
        onChange={(event) => onChange({ input_has_header_row: event.target.checked })}
        styles={{ body: { justifyContent: 'space-between' }, label: { marginBottom: 0 } }}
      />

      <span>
        <StyledInputLabel>Outputs</StyledInputLabel>
        <Stack>
          {fields.data_outputs.map((iterateDataOutput, idx) => (
            <IterateDataOutputFields
              key={iterateDataOutput.output_id}
              iterateDataOutput={iterateDataOutput}
              onChangeNoDelay={(updatedInput: IterateDataOutput) => {
                const updatedInputs = fields.data_outputs.map((input) =>
                  input.output_id === updatedInput.output_id ? updatedInput : input,
                );
                onChangeNoDelay({ data_outputs: updatedInputs });
              }}
              onChangeDelay={(updatedInput: IterateDataOutput) => {
                const updatedInputs = fields.data_outputs.map((input) =>
                  input.output_id === updatedInput.output_id ? updatedInput : input,
                );
                onChange({ data_outputs: updatedInputs });
              }}
              onDelete={() => {
                const updatedInputs = fields.data_outputs.filter(
                  (input) => input.output_id !== iterateDataOutput.output_id,
                );
                onChangeNoDelay({ data_outputs: updatedInputs });
              }}
              step={step}
              inputIndex={idx}
            />
          ))}

          <StepFieldsAddButton
            stepId={step.step_id}
            elementCount={fields.data_outputs.length}
            onClick={() => {
              const newInput: IterateDataOutput = {
                output_id: generateUid(),
                name: '',
                child_data_output: DEFAULT_DYNAMIC_DATA_INPUT,
                output_type: DatasetType.Json,
              };
              onChangeNoDelay({ data_outputs: [...fields.data_outputs, newInput] });
            }}
          />
        </Stack>
      </span>
    </Stack>
  );
};

export default IterateFields;
